import type { CompanyShowcaseData } from '@/data/company-showcase';
import { buildGoogleMapsUrl } from '@/lib/company/google-maps';
import { getCurrentDayOfWeekOrder, type groupCompanyWorkingHours } from '@/lib/company/working-hours';
import { formatPhoneNumber, getPhoneNumberDigits } from '@/lib/phoneNumber';
import { Clock, CreditCard, ExternalLink, MapPin, Navigation, Phone, Sparkles } from 'lucide-react';
import { FaFacebookF, FaInstagram } from 'react-icons/fa';

type CompanyWorkingHourGroup = ReturnType<typeof groupCompanyWorkingHours>[number];

const fallbackPaymentMethods = ['Pix', 'Cartão de crédito', 'Cartão de débito', 'Dinheiro'];

const socialNetworks = [
  {
    href: 'https://www.instagram.com/',
    label: 'Instagram',
    handle: '@empresa',
    icon: FaInstagram,
  },
  {
    href: 'https://www.facebook.com/',
    label: 'Facebook',
    handle: '/empresa',
    icon: FaFacebookF,
  },
];

export interface CompanyInfoContentProps {
  currentDateTime?: Date;
  fullAddress: string;
  latitude?: string | number | null;
  longitude?: string | number | null;
  phoneNumber?: string;
  showcaseData?: CompanyShowcaseData;
  workingHours: CompanyWorkingHourGroup[];
}

export function CompanyInfoContent({
  currentDateTime = new Date(),
  fullAddress,
  latitude,
  longitude,
  phoneNumber,
  showcaseData,
  workingHours,
}: CompanyInfoContentProps) {
  const formattedPhoneNumber = formatPhoneNumber(phoneNumber);
  const phoneNumberDigits = getPhoneNumberDigits(phoneNumber);
  const googleMapsUrl = buildGoogleMapsUrl({ address: fullAddress, latitude, longitude });
  const paymentMethods = showcaseData?.paymentMethods.length ? showcaseData.paymentMethods : fallbackPaymentMethods;
  const currentDayOfWeekOrder = getCurrentDayOfWeekOrder(currentDateTime);

  return (
    <div className="divide-border divide-y">
      <section className="space-y-3 pb-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <MapPin className="text-muted-foreground h-4 w-4" />
            <h3 className="text-card-foreground font-semibold">Localização</h3>
          </div>
          {googleMapsUrl ? (
            <a
              href={googleMapsUrl}
              target="_blank"
              rel="noreferrer"
              aria-label="Abrir localização no Google Maps"
              className="bg-primary/10 text-primary hover:bg-primary/20 flex h-9 w-9 items-center justify-center rounded-full transition-colors"
            >
              <Navigation className="h-4 w-4" />
            </a>
          ) : (
            <span className="bg-primary/10 text-primary flex h-9 w-9 items-center justify-center rounded-full">
              <Navigation className="h-4 w-4" />
            </span>
          )}
        </div>
        <p className="text-muted-foreground text-sm">{fullAddress || 'Endereço não informado'}</p>
        {googleMapsUrl ? (
          <a
            href={googleMapsUrl}
            target="_blank"
            rel="noreferrer"
            className="text-primary inline-flex items-center gap-1.5 text-sm font-medium hover:underline"
          >
            Ver no Google Maps
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        ) : null}
      </section>

      <section className="space-y-3 py-4">
        <div className="flex items-center gap-2">
          <Phone className="text-muted-foreground h-4 w-4" />
          <h3 className="text-card-foreground font-semibold">Contato</h3>
        </div>
        {phoneNumberDigits ? (
          <a
            href={`tel:${phoneNumberDigits}`}
            aria-label={`Ligar para ${formattedPhoneNumber}`}
            className="bg-muted/60 hover:bg-muted flex items-center justify-between gap-3 rounded-xl p-3 transition-colors"
          >
            <span className="text-muted-foreground text-sm">{formattedPhoneNumber}</span>
            <ExternalLink className="text-muted-foreground h-4 w-4" />
          </a>
        ) : (
          <div className="bg-muted/60 flex items-center justify-between gap-3 rounded-xl p-3">
            <span className="text-muted-foreground text-sm">Não informado</span>
          </div>
        )}
      </section>

      <section className="space-y-3 py-4">
        <div className="flex items-center gap-2">
          <Clock className="text-muted-foreground h-4 w-4" />
          <h3 className="text-card-foreground font-semibold">Horário de atendimento</h3>
        </div>
        <div className="space-y-2">
          {workingHours.map((workingHour) => (
            <div key={workingHour.dayOfWeek} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-4 text-sm">
              <span className="text-muted-foreground flex min-w-0 flex-wrap items-center gap-2">
                <span>{workingHour.dayOfWeek}</span>
                {workingHour.order === currentDayOfWeekOrder ? (
                  <span className="bg-primary/10 text-primary rounded-full px-2 py-0.5 text-[10px] font-semibold tracking-wide uppercase">
                    Hoje
                  </span>
                ) : null}
              </span>
              <span className="text-card-foreground text-right font-medium">{workingHour.formattedTimeRanges}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="space-y-3 py-4">
        <div className="flex items-center gap-2">
          <CreditCard className="text-muted-foreground h-4 w-4" />
          <h3 className="text-card-foreground font-semibold">Formas de pagamento</h3>
        </div>
        <div className="flex flex-wrap gap-2">
          {paymentMethods.map((paymentMethod) => (
            <span key={paymentMethod} className="bg-muted text-muted-foreground rounded-full px-3 py-1 text-xs">
              {paymentMethod}
            </span>
          ))}
        </div>
      </section>

      <section className="space-y-3 py-4">
        <div className="flex items-center gap-2">
          <FaInstagram className="text-muted-foreground h-4 w-4" />
          <h3 className="text-card-foreground font-semibold">Redes sociais</h3>
        </div>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-1">
          {socialNetworks.map((socialNetwork) => {
            const SocialIcon = socialNetwork.icon;

            return (
              <a
                key={socialNetwork.label}
                href={socialNetwork.href}
                target="_blank"
                rel="noreferrer"
                aria-label={`Abrir ${socialNetwork.label} da empresa`}
                className="bg-muted/60 hover:bg-muted flex items-center gap-3 rounded-xl p-3.5 transition-colors"
              >
                <SocialIcon className="text-primary h-4 w-4" />
                <span className="min-w-0 flex-1">
                  <span className="text-card-foreground block text-sm font-medium">{socialNetwork.label}</span>
                  <span className="text-muted-foreground block text-xs">{socialNetwork.handle}</span>
                </span>
                <ExternalLink className="text-muted-foreground h-4 w-4" />
              </a>
            );
          })}
        </div>
      </section>

      {showcaseData?.amenities.length ? (
        <section className="space-y-3 py-4">
          <div className="flex items-center gap-2">
            <Sparkles className="text-muted-foreground h-4 w-4" />
            <h3 className="text-card-foreground font-semibold">Comodidades</h3>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {showcaseData.amenities.map((amenity) => (
              <span key={amenity} className="bg-muted text-muted-foreground rounded-lg px-3 py-2 text-center text-xs">
                {amenity}
              </span>
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}

export function CompanyInfoPanel(props: CompanyInfoContentProps) {
  return (
    <aside className="border-border bg-card max-h-[calc(100vh-6rem)] overflow-y-auto overscroll-contain rounded-2xl border p-5 shadow-sm">
      <CompanyInfoContent {...props} />
    </aside>
  );
}
